import { memo, useRef, useState } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import Banner from '@zenbusiness/zui/Banner';
import Chip from '@zenbusiness/zui/Chip';
import FormControl from '@zenbusiness/zui/FormControl';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import { getCountryOptions } from '#client/utils/country';

import { StyledTextField } from './components/TextField.styles';

const options = getCountryOptions();

const CountryUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  value = '',
  dataChangeHandler = () => {},
  disabled
}) => {
  const countryRef = useRef(null);
  // Initialize from the saved value so customers who previously saved a non-US-only
  // countriesOfOperation see the auto-denial warning on reload, not just after editing.
  const [showValidationBanner, setShowValidationBanner] = useState(
    Boolean(value) && !value.split(',').includes('US')
  );
  // Gate validation error on user interaction so in-progress applications don't show red on load.
  const [hasInteracted, setHasInteracted] = useState(false);

  const [countryList, setCountryList] = useState(() => {
    if (!value) {
      return [];
    }
    const countryCodeList = value.split(',');

    return options.filter(
      (option) => countryCodeList.filter((countryCode) => option.code === countryCode).length > 0
    );
  });
  const handleChangeCountryList = (event, newValue) => {
    setCountryList(newValue);
  };

  const trackCountryChangeClick = useClickstreamCallback('buttonPressed');
  const onDataChanged = () => {
    setHasInteracted(true);
    const formatValue = countryList.map((country) => country.code).join(',');
    const isComplete = Boolean(formatValue);

    if (isComplete) {
      field.setLoading();
    }
    dataChangeHandler(
      isComplete,
      { countriesOfOperation: formatValue },
      (forceUpdate, { isSave, isError }) => {
        const hasUS = formatValue.split(',').includes('US');
        setShowValidationBanner(isComplete && !hasUS);
        field.setSaved(isSave && isComplete, isError);
        forceUpdate();
      }
    );

    // Tracking Details: Tracking added to where you conduct business
    if (formatValue) {
      trackCountryChangeClick({
        elementText: formatValue,
        elementName: 'banking_collect_info',
        appPageName: 'banking_application'
      });
    }
  };

  const showError = hasInteracted && countryList.length === 0;

  return (
    <Stack spacing={3}>
      <FormControl
        disabled={disabled}
        fullWidth
      >
        <AutoCompleteInput
          disabled={disabled}
          filterSelectedOptions
          id="country-conduct-business"
          multiple
          onChange={handleChangeCountryList}
          openOnFocus
          options={options}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              error={showError}
              helperText={showError ? 'At least one country of operation is required.' : ''}
              inputProps={params.inputProps}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{ ...params.InputProps }}
              label="Country"
              multiple
              onBlur={onDataChanged}
              ref={countryRef}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.label}
                label={option.label}
                onDelete={() => {
                  // Focus input when delete a country
                  countryRef.current.getElementsByTagName('input')[0].focus();
                  setCountryList(countryList.filter((countryOption) => countryOption !== option));
                }}
                sx={{ margin: '0 2px 0 2px' }}
              />
            ))
          }
          value={countryList}
        />
      </FormControl>

      {showValidationBanner && (
        <Banner
          slots={{
            startIcon: <ErrorOutlineIcon />
          }}
          subtitle="At this time, we are only able to authorize banking for businesses operating only in the United States."
          title="Application will be denied"
          variant="alert"
        />
      )}
    </Stack>
  );
};

CountryUIField.propTypes = {
  value: PropTypes.string,
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func
  }),
  disabled: PropTypes.bool
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.value === nextProp.value &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(CountryUIField, areEqual);
